import { format } from "date-fns";
import { AggregateEsopReport } from "../../../api/esopStats";
import { SecondaryCTAButton } from "../../../components/CTAButtonComponents";

type ActiveAggregateEsopReportsProps = {
  reports: AggregateEsopReport[];
  isLoading: boolean;
  error: string;
  onClose: () => void;
  onRetry: () => void;
  onPreview: (report: AggregateEsopReport) => void;
};

export default function ActiveAggregateEsopReports({
  reports,
  isLoading,
  error,
  onClose,
  onRetry,
  onPreview,
}: ActiveAggregateEsopReportsProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[75vh] w-full max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-lg bg-white shadow-xl sm:max-w-4xl">
        <div className="relative shrink-0 border-b border-gray-200 px-6 py-4">
          <button
            aria-label="Close available ESOP reports"
            className="absolute right-4 top-4 rounded p-1 text-xl leading-none text-gray-500 hover:bg-gray-100 hover:text-gray-800"
            onClick={onClose}
            type="button"
          >
            ×
          </button>
          <h2 className="pr-8 text-lg font-semibold">Available ESOP reports</h2>
          <p className="mt-1 pr-8 text-sm text-gray-500">
            Reports remain available for one day after generation.
          </p>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          {isLoading ? (
            <div className="flex min-h-32 items-center justify-center text-sm text-gray-500">
              Loading available reports…
            </div>
          ) : error ? (
            <div
              className="flex flex-col items-start gap-3 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800 sm:flex-row sm:items-center sm:justify-between"
              role="alert"
            >
              <p>{error}</p>
              <SecondaryCTAButton onClick={onRetry}>
                Try again
              </SecondaryCTAButton>
            </div>
          ) : reports.length === 0 ? (
            <p className="text-sm text-gray-500">No active reports found.</p>
          ) : (
            <div className="divide-y divide-gray-200 rounded border border-gray-200">
              {reports.map((report) => (
                <div
                  className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between"
                  key={report.externalRequestId}
                >
                  <div className="min-w-0 text-sm text-gray-600">
                    <p className="font-medium text-gray-800">
                      {report.fromDate} to {report.toDate}
                    </p>
                    <p className="mt-1">
                      Generated:&nbsp;
                      {report.generatedAt
                        ? format(
                            new Date(report.generatedAt),
                            "dd-MMM-yyyy HH:mm",
                          )
                        : "—"}
                      {" · "}
                      {report.companyCount ?? 0} companies
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      Available until:&nbsp;
                      {report.expiresAt
                        ? format(
                            new Date(report.expiresAt),
                            "dd-MMM-yyyy HH:mm",
                          )
                        : "—"}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-wrap gap-2">
                    <SecondaryCTAButton onClick={() => onPreview(report)}>
                      Preview
                    </SecondaryCTAButton>
                    <SecondaryCTAButton
                      disabled={!report.downloadUrl}
                      onClick={() =>
                        window.open(
                          report.downloadUrl || undefined,
                          "_blank",
                          "noopener,noreferrer",
                        )
                      }
                    >
                      Download
                    </SecondaryCTAButton>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
